import type { ColDef } from 'ag-grid-community';
import { DurationCellRenderer } from '@/components/ag-grid/DurationCellRenderer';

/**
 * Column definitions for SFTP logs grid
 */
const sftpLogsColDef = (): ColDef[] => {
  const colDef: ColDef[] = [
    {
      headerName: 'End Time',
      field: 'end',
      width: 180,
      filter: 'agDateColumnFilter',
      sort: 'desc', // Default sort by end time descending
      cellRenderer: (params: any) => {
        if (!params.value) {
          return 'In Progress';
        }
        // Use a simple date formatting for non-JSX context
        const date = new Date(params.value);
        return date.toLocaleString();
      },
      cellStyle: (params: any) => {
        if (!params.value) {
          return {
            color: '#ed6c02',
            fontStyle: 'italic'
          };
        }
        return null;
      },
    },
    {
      headerName: 'Duration',
      field: 'totalMs',
      width: 120,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      enableValue: true,
      cellRenderer: DurationCellRenderer,
    },
    {
      headerName: 'Entity',
      field: 'entity',
      width: 150,
      filter: 'agSetColumnFilter',
      enableRowGroup: true,
      enablePivot: true,
    },
    {
      headerName: 'Description',
      field: 'description',
      flex: 1,
      minWidth: 300,
      filter: 'agTextColumnFilter',
      tooltipField: 'description',
    },
    {
      headerName: 'Status',
      field: 'success',
      width: 120,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: [true, false, null],
        valueFormatter: (params: any) => {
          const success = params.value;
          if (success === true) return 'Success';
          if (success === false) return 'Failed';
          return 'Indeterminate';
        },
      },
      enableRowGroup: true,
      cellRenderer: (params: any) => {
        const success = params.value;
        const error = params.data?.error;
        const end = params.data?.end;
        
        if (error) {
          return '✗ Error';
        } else if (success === true) {
          return '✓ Success';
        } else if (success === false) {
          return '✗ Failed';
        } else if (!end || success === null) {
          return '⚠ Indeterminate';  
        }
        return '';
      },
      cellStyle: (params: any) => {
        const success = params.value;
        const error = params.data?.error;
        const end = params.data?.end;
        
        if (error || success === false) {
          return { color: '#d32f2f' };
        } else if (success === true) {
          return { color: '#1976d2' };
        } else if (!end || success === null) {
          return { color: '#ed6c02' };
        }
        return null;
      },
    },
    {
      headerName: 'Error',
      field: 'error',
      flex: 1,
      minWidth: 200,
      filter: 'agTextColumnFilter',
      tooltipField: 'error',
      cellRenderer: (params: any) => {
        if (!params.value) return '';
        return params.value;
      },
      cellStyle: (params: any) => {
        if (params.value) {
          return {
            color: '#d32f2f',
            fontSize: '0.875rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          };
        }
        return null;
      },
    },
    {
      headerName: 'Items Processed',
      field: 'itemsProcessed',
      width: 140,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      enableValue: true,
      cellRenderer: (params: any) => {
        if (params.value === null || params.value === undefined) return '0';
        return params.value.toLocaleString();
      },
    },
  ];
  
  return colDef;
};

export default sftpLogsColDef;